
import React, { useEffect, useState, useRef } from 'react';

interface ParticipantVideoProps {
  isLocal: boolean;
  isVideoOff: boolean;
  localVideoRef?: React.RefObject<HTMLVideoElement>;
  localStream?: MediaStream | null;
}

const ParticipantVideo: React.FC<ParticipantVideoProps> = ({ 
  isLocal, 
  isVideoOff, 
  localVideoRef, 
  localStream 
}) => {
  const [error, setError] = useState<string | null>(null);
  const lastStreamRef = useRef<MediaStream | null>(null);
  const hasAttemptedPlay = useRef<boolean>(false);

  useEffect(() => {
    if (isLocal && localVideoRef?.current) {
      try {
        if (localStream && !isVideoOff) {
          console.log("Setting local video stream to element");
          
          // Check if we need to update the stream
          if (localVideoRef.current.srcObject !== localStream) {
            localVideoRef.current.srcObject = localStream;
            lastStreamRef.current = localStream;
            
            // Clear any previous errors
            setError(null);
            
            // Make sure video plays automatically
            if (!hasAttemptedPlay.current) {
              const playPromise = localVideoRef.current.play();
              
              if (playPromise !== undefined) {
                hasAttemptedPlay.current = true;
                
                playPromise.then(() => {
                  console.log("Video playback started successfully");
                  setError(null);
                }).catch(error => {
                  console.error("Failed to play video:", error);
                  setError("Failed to play video. Try toggling your camera again or refresh the page.");
                });
              }
            } else {
              // If we already attempted play before, try again
              localVideoRef.current.play().catch(error => {
                console.error("Failed to play video on reattach:", error);
                setError("Video playback error. Try refreshing the page.");
              });
            }
          }
          
          // Ensure the video is visible when the stream is active
          localVideoRef.current.style.display = 'block';
          localVideoRef.current.style.opacity = '1';
        } else {
          // When video is off, clear the srcObject to fully release the camera
          if (localVideoRef.current.srcObject) {
            console.log("Clearing video source");
            localVideoRef.current.srcObject = null;
            localVideoRef.current.style.display = 'none';
            hasAttemptedPlay.current = false;
          }
        }
      } catch (err) {
        console.error("Error in video element handling:", err);
        setError("Camera error. Please check your browser settings.");
      }
    }

    return () => {
      if (isLocal && localVideoRef?.current && localVideoRef.current.srcObject) {
        console.log("Cleanup: clearing video source in component unmount");
        localVideoRef.current.srcObject = null;
        hasAttemptedPlay.current = false;
      }
    };
  }, [isVideoOff, localStream, isLocal, localVideoRef]);
  
  // For local user with active video and a video reference
  if (isLocal && !isVideoOff && localVideoRef) {
    return (
      <>
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 text-white text-sm p-4 text-center z-30 backdrop-blur-sm border border-red-500/30">
            <p className="max-w-[220px]">{error}</p>
          </div>
        )}
        <video 
          ref={localVideoRef}
          autoPlay 
          playsInline
          muted 
          className="absolute inset-0 w-full h-full object-cover rounded-lg mirror-mode z-10"
          style={{ 
            backgroundColor: '#000', 
            display: isVideoOff ? 'none' : 'block',
            transition: 'opacity 0.3s ease-in-out'
          }}
          onPlaying={() => {
            console.log("Video playback started successfully");
            setError(null);
          }}
          onError={(e) => {
            console.error("Video error:", e);
            setError("Video error occurred. Try toggling your camera or refreshing the page.");
          }}
        />
      </>
    );
  } 
  // For remote users with active video (or placeholder)
  else if (!isVideoOff) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/30 to-neon-blue/20 rounded-lg z-10 overflow-hidden">
        <div className="h-full w-full flex items-center justify-center border border-neon-purple/20">
          <p className="text-white/70 text-sm neon-text-glow">Remote video feed</p>
        </div>
        
        {/* Animated grid lines for technology feel */}
        <div className="absolute inset-0 grid-lines-bg" style={{ backgroundSize: '20px 20px' }}></div>
        
        {/* Neon borders */}
        <div className="absolute inset-0 border border-neon-purple/30 rounded-lg z-20 pointer-events-none"></div>
      </div>
    );
  }
  
  // Return null if video is off
  return null;
};

export default ParticipantVideo;
